import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaTags } from "react-icons/fa";

import {
  getBusinessUnits,
  createBusinessUnit,
  deleteBusinessUnit,
  getDepartments,
  createDepartment,
  deleteDepartment,
  getLocations,
  createLocation,
  deleteLocation,
  getRetentionLabels,
  createRetentionLabel,
  deleteRetentionLabel,
  getUserAccess,
  createUserAccess,
  deleteUserAccess,
  type BusinessUnit,
  type Department,
  type RecordLocation,
  type RetentionLabel,
  type UserAccess,
} from "@/api/physicalRecordsApi";

type TabKey = "businessUnits" | "departments" | "locations" | "retentionLabels" | "userAccess";

const TABS: { key: TabKey; label: string }[] = [
  { key: "businessUnits", label: "Business Units" },
  { key: "departments", label: "Departments" },
  { key: "locations", label: "Locations" },
  { key: "retentionLabels", label: "Retention Labels" },
  { key: "userAccess", label: "User Access" },
];

export default function AdminPhysicalRecordsHome() {
  const [tab, setTab] = useState<TabKey>("businessUnits");
  const [err, setErr] = useState<string | null>(null);

  const [businessUnits, setBusinessUnits] = useState<BusinessUnit[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [locations, setLocations] = useState<RecordLocation[]>([]);
  const [retentionLabels, setRetentionLabels] = useState<RetentionLabel[]>([]);
  const [userAccess, setUserAccess] = useState<UserAccess[]>([]);
  const [loading, setLoading] = useState(true);

  async function loadAll() {
    setLoading(true);
    setErr(null);
    try {
      const [bu, dept, loc, labels, access] = await Promise.all([
        getBusinessUnits(),
        getDepartments(),
        getLocations(),
        getRetentionLabels(),
        getUserAccess(),
      ]);
      setBusinessUnits(bu);
      setDepartments(dept);
      setLocations(loc);
      setRetentionLabels(labels);
      setUserAccess(access);
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e?.message ?? "Failed to load Physical Records admin data.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
  }, []);

  return (
    <div className="container py-4">
      <div className="mb-3 d-flex flex-wrap gap-2 justify-content-between align-items-start">
        <div>
          <h1 className="h4 mb-1">Physical Records Admin</h1>
          <div className="text-muted">
            Manage Business Units, Departments, Locations, Retention Labels, and user access
            assignments for the Physical Records module.
          </div>
        </div>
        <Link className="btn btn-outline-primary text-nowrap" to="/admin/physical-records/categories">
          <FaTags aria-hidden="true" /> Categories
        </Link>
        <Link className="btn btn-outline-primary text-nowrap" to="/admin/physical-records/requests">
          Requests &amp; Transfers
        </Link>
        <Link className="btn btn-outline-primary text-nowrap ms-2" to="/admin/physical-records/disposition">
          Disposition Report
        </Link>
        <Link className="btn btn-outline-primary text-nowrap ms-2" to="/admin/physical-records/migration">
          Migrate Legacy Data
        </Link>
      </div>

      {err && <div className="alert alert-danger">{err}</div>}

      <ul className="nav nav-tabs mb-3">
        {TABS.map((t) => (
          <li className="nav-item" key={t.key}>
            <button
              type="button"
              className={`nav-link ${tab === t.key ? "active" : ""}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          </li>
        ))}
      </ul>

      {loading ? (
        <div>Loading...</div>
      ) : (
        <>
          {tab === "businessUnits" && (
            <BusinessUnitsTab items={businessUnits} onChanged={loadAll} setErr={setErr} />
          )}
          {tab === "departments" && (
            <DepartmentsTab items={departments} businessUnits={businessUnits} onChanged={loadAll} setErr={setErr} />
          )}
          {tab === "locations" && (
            <LocationsTab items={locations} onChanged={loadAll} setErr={setErr} />
          )}
          {tab === "retentionLabels" && (
            <RetentionLabelsTab items={retentionLabels} onChanged={loadAll} setErr={setErr} />
          )}
          {tab === "userAccess" && (
            <UserAccessTab items={userAccess} businessUnits={businessUnits} departments={departments} onChanged={loadAll} setErr={setErr} />
          )}
        </>
      )}
    </div>
  );
}

function BusinessUnitsTab({
  items,
  onChanged,
  setErr,
}: {
  items: BusinessUnit[];
  onChanged: () => void;
  setErr: (msg: string | null) => void;
}) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function onAdd() {
    if (!code.trim() || !name.trim()) {
      setErr("Code and Name are required.");
      return;
    }
    try {
      setSubmitting(true);
      setErr(null);
      await createBusinessUnit({ code: code.trim(), name: name.trim() });
      setCode("");
      setName("");
      onChanged();
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e?.message ?? "Failed to add business unit.");
    } finally {
      setSubmitting(false);
    }
  }

  async function onDelete(id: number) {
    try {
      await deleteBusinessUnit(id);
      onChanged();
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e?.message ?? "Failed to delete business unit.");
    }
  }

  return (
    <div>
      <div className="row g-2 mb-3" style={{ maxWidth: 560 }}>
        <div className="col-4">
          <input className="form-control" placeholder="Code (e.g. EEC)" value={code} onChange={(e) => setCode(e.target.value)} />
        </div>
        <div className="col-6">
          <input className="form-control" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="col-2">
          <button className="btn btn-primary w-100" disabled={submitting} onClick={onAdd}>
            Add
          </button>
        </div>
      </div>

      <table className="table table-sm">
        <thead>
          <tr>
            <th>Code</th>
            <th>Name</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map((b) => (
            <tr key={b.businessUnitId}>
              <td>{b.code}</td>
              <td>{b.name}</td>
              <td className="text-end">
                <button className="btn btn-sm btn-outline-danger" onClick={() => onDelete(b.businessUnitId)}>
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DepartmentsTab({
  items,
  businessUnits,
  onChanged,
  setErr,
}: {
  items: Department[];
  businessUnits: BusinessUnit[];
  onChanged: () => void;
  setErr: (msg: string | null) => void;
}) {
  const [businessUnitId, setBusinessUnitId] = useState<number | "">("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function onAdd() {
    if (!businessUnitId || !name.trim()) {
      setErr("Business Unit and Name are required.");
      return;
    }
    try {
      setSubmitting(true);
      setErr(null);
      await createDepartment({ businessUnitId: Number(businessUnitId), name: name.trim() });
      setName("");
      onChanged();
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e?.message ?? "Failed to add department.");
    } finally {
      setSubmitting(false);
    }
  }

  async function onDelete(id: number) {
    try {
      await deleteDepartment(id);
      onChanged();
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e?.message ?? "Failed to delete department.");
    }
  }

  return (
    <div>
      <div className="row g-2 mb-3" style={{ maxWidth: 560 }}>
        <div className="col-4">
          <select className="form-select" value={businessUnitId} onChange={(e) => setBusinessUnitId(e.target.value ? Number(e.target.value) : "")}>
            <option value="">Business Unit...</option>
            {businessUnits.map((b) => (
              <option key={b.businessUnitId} value={b.businessUnitId}>
                {b.code}
              </option>
            ))}
          </select>
        </div>
        <div className="col-6">
          <input className="form-control" placeholder="Department Name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="col-2">
          <button className="btn btn-primary w-100" disabled={submitting} onClick={onAdd}>
            Add
          </button>
        </div>
      </div>

      <table className="table table-sm">
        <thead>
          <tr>
            <th>Business Unit</th>
            <th>Department</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map((d) => (
            <tr key={d.departmentId}>
              <td>{d.businessUnitCode}</td>
              <td>{d.name}</td>
              <td className="text-end">
                <button className="btn btn-sm btn-outline-danger" onClick={() => onDelete(d.departmentId)}>
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const LOCATION_TYPES = ["IronMountain", "Department", "VirtualCounter"];

function LocationsTab({
  items,
  onChanged,
  setErr,
}: {
  items: RecordLocation[];
  onChanged: () => void;
  setErr: (msg: string | null) => void;
}) {
  const [name, setName] = useState("");
  const [locationType, setLocationType] = useState(LOCATION_TYPES[0]);
  const [submitting, setSubmitting] = useState(false);

  async function onAdd() {
    if (!name.trim()) {
      setErr("Location name is required.");
      return;
    }
    try {
      setSubmitting(true);
      setErr(null);
      await createLocation({ name: name.trim(), locationType });
      setName("");
      onChanged();
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e?.message ?? "Failed to add location.");
    } finally {
      setSubmitting(false);
    }
  }

  async function onDelete(id: number) {
    try {
      await deleteLocation(id);
      onChanged();
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e?.message ?? "Failed to delete location.");
    }
  }

  return (
    <div>
      <div className="row g-2 mb-3" style={{ maxWidth: 560 }}>
        <div className="col-6">
          <input className="form-control" placeholder="Location Name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="col-4">
          <select className="form-select" value={locationType} onChange={(e) => setLocationType(e.target.value)}>
            {LOCATION_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div className="col-2">
          <button className="btn btn-primary w-100" disabled={submitting} onClick={onAdd}>
            Add
          </button>
        </div>
      </div>

      <table className="table table-sm">
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map((l) => (
            <tr key={l.locationId}>
              <td>{l.name}</td>
              <td>{l.locationType}</td>
              <td className="text-end">
                <button className="btn btn-sm btn-outline-danger" onClick={() => onDelete(l.locationId)}>
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RetentionLabelsTab({
  items,
  onChanged,
  setErr,
}: {
  items: RetentionLabel[];
  onChanged: () => void;
  setErr: (msg: string | null) => void;
}) {
  const [name, setName] = useState("");
  const [retentionYears, setRetentionYears] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function onAdd() {
    if (!name.trim()) {
      setErr("Retention label name is required.");
      return;
    }
    try {
      setSubmitting(true);
      setErr(null);
      await createRetentionLabel({
        name: name.trim(),
        retentionYears: retentionYears ? Number(retentionYears) : null,
        description: description.trim() || null,
      });
      setName("");
      setRetentionYears("");
      setDescription("");
      onChanged();
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e?.message ?? "Failed to add retention label.");
    } finally {
      setSubmitting(false);
    }
  }

  async function onDelete(id: number) {
    try {
      await deleteRetentionLabel(id);
      onChanged();
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e?.message ?? "Failed to delete retention label.");
    }
  }

  return (
    <div>
      <div className="row g-2 mb-3" style={{ maxWidth: 720 }}>
        <div className="col-4">
          <input className="form-control" placeholder="Label Name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="col-2">
          <input
            className="form-control"
            type="number"
            placeholder="Years"
            value={retentionYears}
            onChange={(e) => setRetentionYears(e.target.value)}
          />
        </div>
        <div className="col-4">
          <input className="form-control" placeholder="Description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div className="col-2">
          <button className="btn btn-primary w-100" disabled={submitting} onClick={onAdd}>
            Add
          </button>
        </div>
      </div>

      <table className="table table-sm">
        <thead>
          <tr>
            <th>Name</th>
            <th>Retention (yrs)</th>
            <th>Description</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map((r) => (
            <tr key={r.retentionLabelId}>
              <td>{r.name}</td>
              <td>{r.retentionYears ?? "—"}</td>
              <td>{r.description ?? "—"}</td>
              <td className="text-end">
                <button className="btn btn-sm btn-outline-danger" onClick={() => onDelete(r.retentionLabelId)}>
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UserAccessTab({
  items,
  businessUnits,
  departments,
  onChanged,
  setErr,
}: {
  items: UserAccess[];
  businessUnits: BusinessUnit[];
  departments: Department[];
  onChanged: () => void;
  setErr: (msg: string | null) => void;
}) {
  const [userEmail, setUserEmail] = useState("");
  const [businessUnitId, setBusinessUnitId] = useState<number | "">("");
  const [departmentId, setDepartmentId] = useState<number | "">("");
  const [isDirector, setIsDirector] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const filteredDepartments = useMemo(
    () => departments.filter((d) => !businessUnitId || d.businessUnitId === Number(businessUnitId)),
    [departments, businessUnitId]
  );

  async function onAdd() {
    if (!userEmail.trim() || !businessUnitId || !departmentId) {
      setErr("User email, Business Unit, and Department are required.");
      return;
    }
    try {
      setSubmitting(true);
      setErr(null);
      await createUserAccess({
        userEmail: userEmail.trim(),
        businessUnitId: Number(businessUnitId),
        departmentId: Number(departmentId),
        isDirector,
      });
      setUserEmail("");
      setIsDirector(false);
      onChanged();
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e?.message ?? "Failed to add user access.");
    } finally {
      setSubmitting(false);
    }
  }

  async function onDelete(id: number) {
    try {
      await deleteUserAccess(id);
      onChanged();
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e?.message ?? "Failed to remove user access.");
    }
  }

  return (
    <div>
      <div className="row g-2 mb-3 align-items-center" style={{ maxWidth: 820 }}>
        <div className="col-3">
          <input className="form-control" placeholder="User email" value={userEmail} onChange={(e) => setUserEmail(e.target.value)} />
        </div>
        <div className="col-3">
          <select
            className="form-select"
            value={businessUnitId}
            onChange={(e) => {
              setBusinessUnitId(e.target.value ? Number(e.target.value) : "");
              setDepartmentId("");
            }}
          >
            <option value="">Business Unit...</option>
            {businessUnits.map((b) => (
              <option key={b.businessUnitId} value={b.businessUnitId}>
                {b.code}
              </option>
            ))}
          </select>
        </div>
        <div className="col-3">
          <select className="form-select" value={departmentId} onChange={(e) => setDepartmentId(e.target.value ? Number(e.target.value) : "")}>
            <option value="">Department...</option>
            {filteredDepartments.map((d) => (
              <option key={d.departmentId} value={d.departmentId}>
                {d.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-2 form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="isDirector"
            checked={isDirector}
            onChange={(e) => setIsDirector(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="isDirector">
            Director
          </label>
        </div>
        <div className="col-1">
          <button className="btn btn-primary w-100" disabled={submitting} onClick={onAdd}>
            Add
          </button>
        </div>
      </div>

      <table className="table table-sm">
        <thead>
          <tr>
            <th>User</th>
            <th>Business Unit</th>
            <th>Department</th>
            <th>Director</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map((a) => (
            <tr key={a.userAccessId}>
              <td>{a.userEmail}</td>
              <td>{a.businessUnitCode}</td>
              <td>{a.departmentName}</td>
              <td>{a.isDirector ? "Yes" : "No"}</td>
              <td className="text-end">
                <button className="btn btn-sm btn-outline-danger" onClick={() => onDelete(a.userAccessId)}>
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
